import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DERIVED_ROLE_GRANTS } from './derivedRoleGrants';
import { worksWithoutTenant } from './tenantlessRoutes';
import { preferredTenant } from './requireAuthLanding';
import type {
  AccessibleSubTenantDto,
  AuthUserDto,
  TenantMembershipDto,
} from '@oip/contracts';
import { endpoints } from '../api/endpoints';
import { session, setOnTokensRenewed, setUnauthorizedHandler } from '../api/client';

/** Why the session ended, surfaced neutrally on the login page. */
export type SessionEndedReason = 'expired' | 'password-changed';

export const SESSION_ENDED_MESSAGE: Record<SessionEndedReason, string> = {
  expired: 'Your session has ended. Please sign in again.',
  'password-changed': 'Password changed — sign in again.',
};

interface AuthState {
  ready: boolean;
  user: AuthUserDto | null;
  tenantId: string | null;
  memberships: TenantMembershipDto[];
  /**
   * Sub-tenants reachable by DERIVED role — a parent administrator's branches.
   *
   * Empty for everybody else, which is what keeps the hierarchy invisible to a
   * sub-tenant user (D5). A switcher that renders nothing extra when this is
   * empty behaves exactly as it did before the hierarchy existed.
   */
  subTenants: AccessibleSubTenantDto[];
  /** Every tenant the session may enter, grouped the way a person thinks about it. */
  tenantChoices: TenantChoice[];
  /**
   * Set when a PLATFORM ADMINISTRATOR has entered a tenant they are not a
   * member of. Null for ordinary members, whose tenant comes from a membership.
   */
  assumedTenant: { id: string; name: string; isParent: boolean; returnTo?: string } | null;
  /** The partner a platform administrator has entered, or null. */
  assumedPartner: AssumedPartner | null;
  /**
   * What this session may do in the PARTNER console — null when it has no partner at all.
   *
   * ⚠️ **The ONE client-side answer, so no page re-derives it.** Every partner nav item is
   * flagged `whiteLabel: true` and a viewer may open all five pages, so what separates them
   * from an administrator is the WRITE controls — and a per-page role comparison is exactly
   * how a copy of a server rule goes stale here, which this tier has paid for four times.
   *
   * ⚠️ PRESENTATION ONLY. `WhiteLabelContextGuard` refuses every non-GET from a viewer, so a
   * client that got this wrong would show itself buttons that 403 rather than gain anything.
   */
  partnerRole: 'WL_ADMIN' | 'WL_VIEWER' | null;
  /** Enter a tenant as a platform administrator. Audited server-side. */
  /**
   * ⚠️ `isParent` is REQUIRED, so a call site cannot forget it.
   *
   * An assumed tenant holds no membership, so it is absent from
   * `tenantChoices` — which is where every other screen reads `isParent` from.
   * Without it `useInOrganisation()` answered FALSE for a platform
   * administrator inside a company, and the whole "an organisation holds
   * nothing" explanation silently did not apply to the one person most likely
   * to be looking at it. Both existing callers already know the answer; making
   * it required means a third is a compile error rather than a screen that
   * quietly reverts to the old defect.
   */
  assumeTenant: (tenant: { id: string; name: string; isParent: boolean }) => void;
  assumePartner: (partner: AssumedPartner) => void;
  exitAssumedPartner: () => void;
  /** Leave an assumed tenant and return to platform scope. */
  exitAssumedTenant: () => void;
  role: string | null;
  /** Set when a session was ended for us rather than by an explicit sign-out. */
  sessionEndedReason: SessionEndedReason | null;
  clearSessionEndedReason: () => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  /** Ends the session and records why. Used by the 401 handler and by
   *  change-password, both of which invalidate every outstanding token. */
  endSession: (reason: SessionEndedReason) => void;
  setTenant: (tenantId: string) => void;
  /**
   * Re-reads `me`, keeping the active tenant.
   *
   * Needed because the switcher's contents come from `me` rather than from a
   * react-query cache: after creating a sub-tenant the branch exists and there
   * is no way to reach it until something refetches. Preserves `tenantId`, so
   * refreshing never moves the user.
   */
  refreshUser: () => Promise<void>;
}

/** One entry in the tenant switcher. */
export interface TenantChoice {
  tenantId: string;
  tenantName: string;
  isParent: boolean;
  /** The customer this belongs to, when known. Null for a parent, or a lone tenant. */
  parentTenantId: string | null;
  /** True when access comes from a parent membership rather than an explicit one. */
  derived: boolean;
  /**
   * Whether this is somewhere the session can actually go.
   *
   * A suspended or archived tenant is listed but not enterable. Offering it
   * unmarked was a door into a room with no floor: every panel inside answers
   * 403 and the branding falls back to the platform's, which reads as a broken
   * product rather than a paused branch. Listing it and saying why is the point
   * — silently dropping it would leave an administrator wondering where the
   * branch went.
   */
  state: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
}

const AuthContext = createContext<AuthState | null>(null);
const TOKEN_KEY = 'oip.token';
/**
 * The refresh token.
 *
 * Kept beside the access token in localStorage, which is consistent with what
 * this client already does — and a deliberate trade rather than an oversight. A
 * cookie would resist XSS better, but the API is Bearer-based so a native client
 * could not use one, and the mitigations sit on the server: single-use rotation
 * and revocation of the whole family the moment a spent token reappears.
 */
const REFRESH_KEY = 'oip.refreshToken';
const TENANT_KEY = 'oip.tenant';
/**
 * Stored separately from TENANT_KEY because it holds a tenant the user is NOT
 * a member of — restoring it on boot must not be confused with restoring a
 * remembered membership.
 */
const ASSUMED_KEY = 'oip.assumedTenant';
const ASSUMED_PARTNER_KEY = 'oip.assumedPartner';

/**
 * Where a platform administrator is standing inside somebody else's hierarchy.
 *
 * `resellerId` null means the PARTNER's own level — the same shape `WhiteLabelContext`
 * carries, and the same trap: read as "no reseller" it would scope a partner-level view to
 * nothing. The names are carried so the banner can say where you are without a fetch.
 */
export interface AssumedPartner {
  id: string;
  name: string;
  resellerId: string | null;
  resellerName: string | null;
  /** Where the reader was standing when they entered. See `returnPath`. */
  returnTo?: string;
}

/**
 * Where "Leave" should put somebody back.
 *
 * ⚠️ **Captured HERE rather than passed by each caller.** `assumeTenant` has four call
 * sites and `assumePartner` one; a required argument is one a later call site can supply
 * wrongly, and an optional one is one it will forget — the argument the audit trail's
 * ambient `ipAddress` already settled, where a field populated only where somebody
 * remembered could not be read at all.
 *
 * ⚠️ **Only a path that WORKS WITHOUT A TENANT is remembered.** Leaving is what removes the
 * context, so returning to a tenant-scoped page would put the reader back on one that can no
 * longer work — the very thing the redirect in `routes.tsx` exists to prevent, reintroduced
 * by the control meant to help. Anything else records nothing and that redirect still
 * handles it.
 */
function returnPath(): string | undefined {
  const here = `${window.location.pathname}${window.location.search}`;
  return worksWithoutTenant(window.location.pathname) ? here : undefined;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<AuthUserDto | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [sessionEndedReason, setSessionEndedReason] = useState<SessionEndedReason | null>(null);
  const [assumedTenant, setAssumedTenant] = useState<{
    id: string;
    name: string;
    isParent: boolean;
    returnTo?: string;
  } | null>(null);
  /**
   * The PARTNER a platform administrator has entered.
   *
   * ⚠️ Separate from `assumedTenant` rather than folded into it, and the two are NOT
   * mutually exclusive: entering a partner's company from inside their console sets both,
   * and collapsing them would make leaving one silently leave the other.
   */
  const [assumedPartner, setAssumedPartner] = useState<AssumedPartner | null>(null);

  const applyTenant = useCallback((id: string | null) => {
    session.tenantId = id;
    setTenantId(id);
    if (id) localStorage.setItem(TENANT_KEY, id);
    /*
     * Deliberately does NOT re-read `me`.
     *
     * `me` does carry `features` and `units`, but nothing reads them from there:
     * both have their own tenant-keyed queries (`useFeatures`, `useUnits`) that
     * refetch on their own when this changes, which is what makes a grant by a
     * platform administrator reach an open tab. Refreshing here would be a
     * second request for values already arriving.
     *
     * What `me` carries that a switch cannot change — memberships and the
     * sub-tenant list — is refreshed explicitly by `refreshUser` where it
     * genuinely changes, which is when a sub-tenant is created.
     */
  }, []);

  /**
   * Where a session should land, given what it remembers.
   *
   * One function, used on boot AND when leaving an assumed tenant, because those
   * two disagreeing is exactly the bug below.
   *
   * A tenant administrator is a member of the parent AND of their sub-tenant.
   * Taking the first membership would put them in the parent — a management
   * context with no vehicles, no incidents and no telemetry — which looks
   * exactly like their data having disappeared. Prefer a sub-tenant, and fall
   * back to the parent only for somebody who has nothing else, which is what the
   * server infers too.
   */
  const preferredTenantId = useCallback((u: AuthUserDto | null): string | null => {
    if (!u) return null;
    /*
     * A remembered tenant counts if the session can REACH it — which includes a
     * branch reached by derived role, not only an explicit membership.
     *
     * Checking memberships alone meant a parent administrator who switched to a
     * branch was silently returned to the organisation on every page reload:
     * within the app it worked, and refreshing, following a link or reopening a
     * tab put them somewhere else without saying so. The fleet page then showed
     * no vehicles, because a parent holds none — which reads as data loss.
     */
    // Only tenants that can actually be entered. A suspended or archived one is
    // refused by the guard, so landing in it would render an application in
    // which every request fails.
    const usable = u.memberships.filter((m) => m.tenantState === 'ACTIVE');

    const saved = localStorage.getItem(TENANT_KEY);
    const reachable =
      saved !== null &&
      (usable.some((m) => m.tenantId === saved) ||
        // Same rule for a derived branch: a parent administrator who was inside
        // one when it was suspended must not be put back into it on reload.
        u.subTenants.some((sub) => sub.tenantId === saved && sub.tenantState === 'ACTIVE'));

    // Computed and discarded: `preferredTenant` applies the rule, and this is
    // kept only because the comment above it is the clearest statement of it.
    void reachable;
    /*
     * Delegated so the rule is tested, and so it MATCHES `inferActiveTenant`.
     *
     * It used to end `?? usable[0]?.tenantId`, which picked the first
     * organisation arbitrarily where the server had declined to guess between
     * several — see `preferredTenant` for what that did to a partner
     * administrator.
     */
    return preferredTenant({
      memberships: u.memberships.map((m) => ({
        tenantId: m.tenantId,
        tenantType: m.tenantType,
        tenantState: (m.tenantState ?? 'ACTIVE') as 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED',
      })),
      saved,
      /*
       * Everything reachable WITHOUT a membership: branches under a parent this
       * person administers, and — since the partner tier — every company and
       * fleet account under a white-label partner they administer.
       *
       * Missing the partner half lost the tenant on every page reload. Within
       * the app switching worked; refreshing, following a link or reopening a
       * tab returned them to no tenant at all, because a remembered company was
       * in neither `memberships` nor `subTenants` and so did not count as
       * reachable. Exactly the failure this list was widened for once already,
       * one tier down. Found by reloading, not by a test.
       */
      derived: [
        ...u.subTenants.map((sub) => ({
          tenantId: sub.tenantId,
          tenantState: sub.tenantState ?? 'ACTIVE',
        })),
        ...(u.whiteLabels ?? []).flatMap((wl) =>
          wl.companies.flatMap((c) => [
            { tenantId: c.tenantId, tenantState: c.tenantState },
            ...c.fleetAccounts.map((r) => ({ tenantId: r.tenantId, tenantState: r.tenantState })),
          ]),
        ),
      ],
    });
  }, []);

  const hydrate = useCallback((u: AuthUserDto) => {
    setUser(u);
    // A platform administrator may have entered a tenant they do not belong
    // to; that choice lives under its own key and is restored first.
    /*
     * ⚠️ Restored BEFORE the tenant, and gated on `isPlatformAdmin` exactly as that one is:
     * a stored value must not survive somebody's capability being revoked. It is separate
     * from the tenant restore rather than nested in it, because entering a partner and
     * entering one of its companies are independent acts — a reader can be in both.
     */
    const savedPartner = localStorage.getItem(ASSUMED_PARTNER_KEY);
    if (u.isPlatformAdmin && savedPartner) {
      try {
        const parsed = JSON.parse(savedPartner) as AssumedPartner;
        if (typeof parsed?.id === 'string') {
          setAssumedPartner(parsed);
          session.partnerId = parsed.id;
          session.resellerId = parsed.resellerId ?? null;
        }
      } catch {
        // An unreadable value falls back to NOT being anywhere, never to a guess.
        localStorage.removeItem(ASSUMED_PARTNER_KEY);
      }
    } else if (savedPartner) {
      localStorage.removeItem(ASSUMED_PARTNER_KEY);
    }

    const savedAssumed = localStorage.getItem(ASSUMED_KEY);
    if (u.isPlatformAdmin && savedAssumed) {
      try {
        const parsed = JSON.parse(savedAssumed) as {
          id: string;
          name: string;
          isParent?: boolean;
        };
        if (parsed?.id) {
          /*
           * ⚠️ `isParent` is OPTIONAL when read back, and defaults to FALSE.
           * A record written before this field existed has none, and reading
           * an absent value as "parent" would put the organisation notice on
           * a fleet account's dashboard — an explanation for an emptiness that is
           * not there. The wrong answer in this direction is silent; in the
           * other it is simply the previous behaviour.
           */
          setAssumedTenant({ ...parsed, isParent: parsed.isParent === true });
          session.tenantId = parsed.id;
          setTenantId(parsed.id);
          setReady(true);
          return;
        }
      } catch {
        localStorage.removeItem(ASSUMED_KEY);
      }
    }

    const chosen = preferredTenantId(u);
    session.tenantId = chosen;
    setTenantId(chosen);
  }, [preferredTenantId]);

  /*
   * The client renews tokens on a 401 without the provider being involved, so
   * the new pair has to be written down — otherwise a reload would present the
   * spent refresh token, the server would read that as reuse, and the user
   * would be signed out for having left the tab open.
   */
  useEffect(() => {
    setOnTokensRenewed(({ accessToken, refreshToken }) => {
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_KEY, refreshToken);
    });
    return () => setOnTokensRenewed(null);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setReady(true);
      return;
    }
    session.token = token;
    session.refreshToken = localStorage.getItem(REFRESH_KEY);
    endpoints
      .me()
      .then((me) => hydrate(me))
      .catch(() => {
        // The client already tried to renew before this rejected, so reaching
        // here means the refresh token is gone too.
        session.token = null;
        session.refreshToken = null;
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
      })
      .finally(() => setReady(true));
  }, [hydrate]);

  const login = useCallback(
    async (email: string, password: string) => {
      setSessionEndedReason(null);
      const res = await endpoints.login(email, password);
      session.token = res.accessToken;
      session.refreshToken = res.refreshToken;
      localStorage.setItem(TOKEN_KEY, res.accessToken);
      localStorage.setItem(REFRESH_KEY, res.refreshToken);
      hydrate(res.user);
    },
    [hydrate],
  );

  /**
   * Tears down the session.
   *
   * `keepTenantChoice` preserves the remembered tenant when the session ended
   * for us (expiry, revocation, password change) so the user lands back where
   * they were after signing in. An explicit sign-out clears it.
   */
  const teardown = useCallback(
    (keepTenantChoice: boolean) => {
      session.token = null;
      session.refreshToken = null;
      session.tenantId = null;
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      if (!keepTenantChoice) localStorage.removeItem(TENANT_KEY);
      // An assumed tenant is never preserved across a session end — re-entering
      // must be a deliberate act, and is audited each time.
      localStorage.removeItem(ASSUMED_KEY);
      setAssumedTenant(null);
      // Same rule for a partner: re-entering must be a deliberate act, and is audited.
      localStorage.removeItem(ASSUMED_PARTNER_KEY);
      setAssumedPartner(null);
      session.partnerId = null;
      session.resellerId = null;
      setUser(null);
      setTenantId(null);
      // Authenticated data must not survive the session that fetched it.
      queryClient.clear();
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    setSessionEndedReason(null);
    /*
     * Revoke server-side, then tear down locally regardless of the outcome.
     *
     * Fire-and-forget on purpose: clearing localStorage alone would leave a
     * refresh token valid for thirty days, so signing out on a shared machine
     * would look like it worked and would not have. But a failed revoke must
     * not trap someone in a session they asked to leave, so the local teardown
     * does not wait for it.
     */
    const refreshToken = session.refreshToken ?? undefined;
    void endpoints.logout(refreshToken).catch(() => undefined);
    teardown(false);
  }, [teardown]);

  const endSession = useCallback(
    (reason: SessionEndedReason) => {
      // Concurrent 401s all land here; the guard makes the teardown idempotent
      // so the cache is cleared once and RequireAuth redirects once.
      if (!session.token) return;
      setSessionEndedReason(reason);
      teardown(true);
    },
    [teardown],
  );

  const clearSessionEndedReason = useCallback(() => setSessionEndedReason(null), []);

  const assumeTenant = useCallback(
    (tenant: { id: string; name: string; isParent: boolean }) => {
      // Cached tenant data belongs to whichever tenant was active before, so it
      // must not survive the switch.
      queryClient.clear();
      const withReturn = { ...tenant, returnTo: returnPath() };
      localStorage.setItem(ASSUMED_KEY, JSON.stringify(withReturn));
      setAssumedTenant(withReturn);
      session.tenantId = tenant.id;
      setTenantId(tenant.id);
    },
    [queryClient],
  );

  const assumePartner = useCallback(
    (partner: AssumedPartner) => {
      // Cached data belongs to wherever the reader was before, so it must not survive.
      queryClient.clear();
      const withReturn = { ...partner, returnTo: returnPath() };
      localStorage.setItem(ASSUMED_PARTNER_KEY, JSON.stringify(withReturn));
      setAssumedPartner(withReturn);
      session.partnerId = partner.id;
      session.resellerId = partner.resellerId;
    },
    [queryClient],
  );

  const exitAssumedPartner = useCallback(() => {
    queryClient.clear();
    localStorage.removeItem(ASSUMED_PARTNER_KEY);
    setAssumedPartner(null);
    session.partnerId = null;
    session.resellerId = null;
  }, [queryClient]);

  const exitAssumedTenant = useCallback(() => {
    queryClient.clear();
    localStorage.removeItem(ASSUMED_KEY);
    setAssumedTenant(null);
    /*
     * Back to their OWN tenant, not to nothing.
     *
     * This cleared `tenantId` unconditionally, and `role` is derived by matching
     * `tenantId` against memberships — so a platform administrator who also holds
     * memberships left an assumed tenant and was stranded with no tenant context
     * and no role until they reloaded the page. Restoring the same tenant boot
     * would have chosen means leaving is the exact inverse of entering.
     */
    const restored = preferredTenantId(user);
    session.tenantId = restored;
    setTenantId(restored);
  }, [queryClient, preferredTenantId, user]);

  const refreshUser = useCallback(async () => {
    const me = await endpoints.me();
    setUser(me);
  }, []);

  // One central subscription: any authenticated 401 ends the session.
  useEffect(() => {
    setUnauthorizedHandler(() => endSession('expired'));
    return () => setUnauthorizedHandler(null);
  }, [endSession]);

  /*
   * The role in the ACTIVE tenant.
   *
   * An explicit membership wins (D3a). Failing that, a tenant that appears in
   * `subTenants` is one this person reaches by parent authority, and the server
   * will resolve PARENT_CHILD_ADMIN for it — so the client says the same thing.
   * Leaving it null instead would hide every control in a context the server
   * serves perfectly well, which reads as a broken page rather than a
   * permission.
   */
  const role = useMemo(() => {
    const membership = user?.memberships.find((m) => m.tenantId === tenantId);
    if (membership) return membership.role as string;
    if (user?.subTenants.some((s) => s.tenantId === tenantId)) return 'PARENT_CHILD_ADMIN';
    /*
     * The same rule one tier up: a company or fleet account reachable through a
     * white-label partner resolves to WL_CHILD_ADMIN, exactly as
     * `TenantContextGuard` resolves it server-side.
     *
     * Missing this made the whole application invisible to a partner
     * administrator who had just entered one of their own companies: the header
     * named the company, the server served every route, and the sidebar showed
     * nothing — because `role` was null, so `isNavItemVisible` concluded there
     * was no tenant context at all. Reported by driving it, and it is the third
     * time this exact shape has appeared. Anything that gates on a role must go
     * through here.
     */
    const throughPartner = (user?.whiteLabels ?? []).some((wl) =>
      wl.companies.some(
        (c) => c.tenantId === tenantId || c.fleetAccounts.some((r) => r.tenantId === tenantId),
      ),
    );
    if (throughPartner) return 'WL_CHILD_ADMIN';
    return null;
  }, [user, tenantId]);

  /*
   * The switcher's model, built once here rather than in the component.
   *
   * Grouped by customer, because after the migration a flat list shows a tenant
   * administrator "AjnaView" and "Default" as peers, which describes nothing.
   */
  const tenantChoices = useMemo<TenantChoice[]>(() => {
    if (!user) return [];
    const byId = new Map<string, TenantChoice>();
    for (const m of user.memberships) {
      byId.set(m.tenantId, {
        tenantId: m.tenantId,
        tenantName: m.tenantName,
        // Carried on the membership itself. Inferring it from `subTenants`
        // cannot work: a sub-tenant the user is an explicit member of is
        // excluded from that list, so their own branch would have looked
        // unrelated to the organisation directly above it.
        isParent: m.tenantType === 'PARENT',
        parentTenantId: m.parentTenantId,
        derived: false,
        state: m.tenantState,
      });
    }
    /*
     * Companies and fleet accounts reached by administering a WHITE-LABEL PARTNER.
     *
     * A partner administrator holds no tenant membership at all, so without
     * these the switcher and the chooser would both be empty for somebody who
     * can reach dozens of organisations. Added BEFORE `subTenants` only in the
     * sense that `byId` de-duplicates: an explicit membership already in the map
     * always wins, because it carries the person's real role.
     */
    for (const wl of user.whiteLabels ?? []) {
      for (const company of wl.companies) {
        if (!byId.has(company.tenantId)) {
          byId.set(company.tenantId, {
            tenantId: company.tenantId,
            tenantName: company.tenantName,
            isParent: true,
            parentTenantId: null,
            derived: true,
            state: company.tenantState,
          });
        }
        for (const fleetAccount of company.fleetAccounts) {
          if (byId.has(fleetAccount.tenantId)) continue;
          byId.set(fleetAccount.tenantId, {
            tenantId: fleetAccount.tenantId,
            tenantName: fleetAccount.tenantName,
            isParent: false,
            // Grouped under its COMPANY, not its partner: a fleet account belongs to
            // the company, and grouping by partner would flatten forty
            // fleet accounts into one unreadable list.
            parentTenantId: company.tenantId,
            derived: true,
            state: fleetAccount.tenantState,
          });
        }
      }
    }

    for (const sub of user.subTenants) {
      if (byId.has(sub.tenantId)) continue;
      byId.set(sub.tenantId, {
        tenantId: sub.tenantId,
        tenantName: sub.tenantName,
        isParent: false,
        parentTenantId: sub.parentTenantId,
        derived: true,
        state: sub.tenantState,
      });
    }
    return [...byId.values()];
  }, [user]);

  /*
   * Switching, refused for a tenant that is not active.
   *
   * Here rather than in the switcher, because the switcher is not the only
   * caller — the Sub-tenants page opens a branch too, and a disabled MUI menu
   * item still fires the Select's change handler when something reaches it by
   * other means. Presentation says why; this is what makes it true.
   *
   * An id that is not a known choice passes through untouched: that is the
   * platform administrator assuming a tenant they hold no membership in, which
   * is a different mechanism with its own rules.
   */
  const setTenant = useCallback(
    (id: string) => {
      const choice = tenantChoices.find((c) => c.tenantId === id);
      if (choice && choice.state !== 'ACTIVE') return;
      applyTenant(id);
    },
    [tenantChoices, applyTenant],
  );

  /*
   * ⚠️ **A PLATFORM ADMINISTRATOR WHO ENTERED A PARTNER ACTS**, so they resolve to
   * `WL_ADMIN` — the server says the same thing (`role: 'WL_ADMIN'` on the assume path),
   * and admitting them read-only would make the assume path nearly pointless for the person
   * investigating a support call.
   *
   * ⚠️ `every`, not `some`: a session is read-only only when NONE of its partner
   * memberships can write. One administering membership restores the controls, which is the
   * same direction `resellerScoped` above resolves and for the same reason.
   */
  const partnerRole = useMemo<'WL_ADMIN' | 'WL_VIEWER' | null>(() => {
    if (assumedPartner) return 'WL_ADMIN';
    const wl = user?.whiteLabels ?? [];
    if (wl.length === 0) return null;
    /*
     * ⚠️ Absent means `WL_ADMIN`, matching the contract's own default. An OLDER SERVER never
     * sent this field and never listed anybody but administrators, so reading absence as
     * "viewer" would strip every write control from a working console the moment a new
     * bundle met an old API.
     */
    return wl.every((w) => w.role === 'WL_VIEWER') ? 'WL_VIEWER' : 'WL_ADMIN';
  }, [assumedPartner, user?.whiteLabels]);

  const value = useMemo<AuthState>(
    () => ({
      ready,
      user,
      tenantId,
      memberships: user?.memberships ?? [],
      subTenants: user?.subTenants ?? [],
      tenantChoices,
      assumedTenant,
      assumedPartner,
      partnerRole,
      assumeTenant,
      assumePartner,
      exitAssumedPartner,
      exitAssumedTenant,
      role,
      sessionEndedReason,
      clearSessionEndedReason,
      login,
      logout,
      endSession,
      setTenant,
      refreshUser,
    }),
    [
      ready,
      user,
      tenantId,
      tenantChoices,
      assumedTenant,
      assumedPartner,
      partnerRole,
      assumeTenant,
      assumePartner,
      exitAssumedPartner,
      exitAssumedTenant,
      role,
      sessionEndedReason,
      clearSessionEndedReason,
      login,
      logout,
      endSession,
      setTenant,
      refreshUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * Auth state when there is any, `null` when there is not.
 *
 * For PRESENTATION code that must render either way — the theme provider is the
 * case that prompted it: it needs the tenant to resolve branding, but a component
 * test or a story mounting it alone should paint with defaults rather than crash.
 *
 * `useAuth` keeps throwing, deliberately. Anything making an authorisation
 * decision wants the loud failure; only code that genuinely has a sensible
 * unauthenticated rendering should reach for this.
 */
export function useOptionalAuth(): AuthState | null {
  return useContext(AuthContext);
}

/** Role gate helper shared by nav + pages. */
export function hasRole(role: string | null, ...allowed: string[]): boolean {
  if (role === null) return false;
  if (allowed.includes(role)) return true;
  const derived = DERIVED_ROLE_GRANTS[role];
  return derived !== undefined && allowed.some((a) => derived.includes(a));
}
